import React from 'react';
import { GridItem, GridItemValue, GridSection } from './constants';

interface NodeInfoGridPanelProps {
  header?: string;
  sections?: GridSection[];
  renderItem?: (item: GridItem, index: number) => React.ReactNode;
  ColumnTemplate?: string;
  columnsPerRow?: number;
  gap?: string;
  padding?: string;
}

/**
 * - converts % to fr
 * - enforces minmax(0, fr)
 */
const normalizeTemplate = (template?: string) => {
  if (!template) {
    return undefined;
  }

  return template
    .replace(/(\d+)%/g, (_, p) => `${Number(p)}fr`)
    .replace(/\b(\d+)fr\b/g, 'minmax(0, $1fr)');
};

const labelClass = 'min-w-0 break-words text-[14px] text-[#5D5D5D]';
const valueClass =
  'min-w-0 max-w-full overflow-hidden [overflow-wrap:anywhere] whitespace-normal text-[14px] text-[#313131]';

const renderLabelValue = (
  label: React.ReactNode,
  value: React.ReactNode,
  span?: number,
  testIdPrefix?: string,
) => (
  <React.Fragment>
    <div
      className={labelClass}
      data-testid={testIdPrefix ? `${testIdPrefix}-label` : undefined}
    >
      {label ?? '-'}:
    </div>
    <div
      className={valueClass}
      style={{ gridColumn: span ? `span ${span}` : undefined }}
      data-testid={testIdPrefix ? `${testIdPrefix}-value` : undefined}
    >
      {value ?? '-'}
    </div>
  </React.Fragment>
);

const isGridItem = (value: unknown): value is GridItem =>
  !!value &&
  typeof value === 'object' &&
  ('labelName' in (value as GridItem) || 'info' in (value as GridItem));

const renderItemValue = (value?: GridItemValue, testIdPrefix?: string) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '-';
    }

    const nestedItems = value.filter(isGridItem);
    if (nestedItems.length === value.length) {
      return (
        <div
          className="grid gap-x-2 gap-y-1"
          style={{ gridTemplateColumns: 'auto minmax(0, 1fr)' }}
        >
          {nestedItems.map((nestedItem, nestedIndex) => (
            <React.Fragment key={nestedIndex}>
              {renderLabelValue(
                nestedItem.labelName,
                nestedItem.info,
                nestedItem.span,
                testIdPrefix ? `${testIdPrefix}-nested-${nestedIndex}` : undefined,
              )}
            </React.Fragment>
          ))}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        {value.map((entry, entryIndex) => (
          <div
            key={entryIndex}
            className={valueClass}
            data-testid={testIdPrefix ? `${testIdPrefix}-entry-${entryIndex}` : undefined}
          >
            {entry ?? '-'}
          </div>
        ))}
      </div>
    );
  }

  return value ?? '-';
};

const NodeInfoGridPanel: React.FC<NodeInfoGridPanelProps> = ({
  header,
  sections = [],
  renderItem,
  ColumnTemplate,
  columnsPerRow = 4,
  gap = '8px',
  padding = '10px',
}) => {
  return (
    <div
      className="overflow-y-auto flex flex-col w-full h-[82%] box-border"
      style={{ padding }}
    >
      {header && (
        <div className="nodeInfo nodeLabel col-6 mb-2" data-testid="node-info-header">
          {header}
        </div>
      )}

      {sections.map((section, sectionIndex) => {
        const safeItems = section.items ?? [];

        const template =
          normalizeTemplate(ColumnTemplate) ??
          `repeat(${section.columnsPerRow ?? columnsPerRow}, minmax(0, 1fr))`;

        return (
          <div key={sectionIndex} className="pb-2">
            {section.sectionTitle && (
              <div
                className="section-title text-sm text-[#5D5D5D] pb-2"
                data-testid={`node-info-section-title-${sectionIndex}`}
              >
                {section.sectionTitle}
              </div>
            )}

            <div
              className="grid-body grid min-w-0 w-full box-border"
              style={{ gridTemplateColumns: template, gap }}
            >
              {safeItems.length
                ? safeItems.map((item, index) => {
                    const testIdPrefix = `node-info-item-${sectionIndex}-${index}`;
                    return renderItem ? (
                      <React.Fragment key={index}>
                        {renderItem(item, index)}
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={index}>
                        {renderLabelValue(
                          item.labelName,
                          renderItemValue(item.info, testIdPrefix),
                          item.span,
                          testIdPrefix,
                        )}
                      </React.Fragment>
                    );
                  })
                : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NodeInfoGridPanel;
